import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { FileText } from 'lucide-react'
import { apiErrorDetail } from '@/lib/api/fraud-client'
import { useAdminUpdateChargeback, useFraudAllChargebacks } from '@/lib/api/fraud-queries'
import { CHARGEBACK_STATES, type ChargebackState, type StaffChargeback } from '@/lib/api/fraud-types'
import { cn } from '@/lib/utils'
import { Banner, FinixLink, Spinner } from '../shared'
import { EMPTY, PILL, ROW, TD, TH, fmtDateTime, moneyFormatter } from '../format'
import { RESPONSE_FILTERS, daysUntil, responseStatus, type ResponseGroup } from './dispute-response'
import { DisputeReviewModal } from './dispute-review'

const STATE_CHIP: Record<ChargebackState, string> = {
  received: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
  won: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
  lost: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  accepted: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
}

const SELECT = 'rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-1.5 text-sm'

/**
 * Every chargeback across events, with how each Finix dispute is being
 * answered: the drafted recommendation, the evidence PDF and whether it
 * went to Finix. A row opens its review (`?dispute=<id>`), where staff
 * answer it. Recording one stays on the event's page, where the reload
 * picker lives.
 */
export function AdminChargebacks({ openDisputeId, onOpenDispute }: {
  openDisputeId: number | null
  onOpenDispute: (chargebackId: number | null) => void
}) {
  const [state, setState] = useState<'' | ChargebackState>('')
  const [response, setResponse] = useState<'' | ResponseGroup>('')
  const { data, isLoading, error } = useFraudAllChargebacks(state ? { state } : undefined)
  const update = useAdminUpdateChargeback()
  const [rowError, setRowError] = useState<string | null>(null)
  const [pendingId, setPendingId] = useState<number | null>(null)
  const rows = useMemo(
    () => (data ?? []).filter((c) => !response || responseStatus(c)?.group === response),
    [data, response],
  )
  const awaiting = useMemo(() => (data ?? []).filter((c) => responseStatus(c)?.group === 'open').length, [data])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <select value={state} onChange={(e) => setState(e.target.value as '' | ChargebackState)} className={SELECT}>
            <option value="">All states</option>
            {CHARGEBACK_STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={response} onChange={(e) => setResponse(e.target.value as '' | ResponseGroup)} className={SELECT}>
            {RESPONSE_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}{f.value === 'open' && awaiting ? ` (${awaiting})` : ''}
              </option>
            ))}
          </select>
        </div>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Finix disputes are imported and drafted daily. Open a row to review and answer it. Record one before it lands from the event's Chargebacks tab.
        </p>
      </div>

      {rowError && <Banner kind="error">{rowError}</Banner>}

      <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 overflow-x-auto">
        {error ? (
          <p className="p-6 text-sm text-red-600 dark:text-red-400">Failed to load chargebacks: {error.message}</p>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-neutral-500 dark:text-neutral-400">No chargebacks{state || response ? ' match these filters' : ''}.</p>
        ) : (
          <table className="w-full whitespace-nowrap">
            <thead>
              <tr className="border-b border-neutral-200 dark:border-neutral-800">
                <th className={TH}>Received</th>
                <th className={TH}>Event</th>
                <th className={TH}>Attendee</th>
                <th className={TH}>Amount</th>
                <th className={TH}>State</th>
                <th className={TH}>Reason</th>
                <th className={TH}>Respond by</th>
                <th className={TH}>Our response</th>
                <th className={TH}>Evidence</th>
                <th className={TH}>Dispute</th>
                <th className={TH}>Transfer</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <ChargebackRow
                  key={c.id}
                  c={c}
                  pending={pendingId === c.id}
                  onOpen={() => onOpenDispute(c.id)}
                  onState={async (next) => {
                    setRowError(null)
                    setPendingId(c.id)
                    try {
                      await update.mutateAsync({ chargebackId: c.id, data: { state: next } })
                    } catch (err) {
                      setRowError(apiErrorDetail(err, 'Could not update the chargeback.'))
                    } finally {
                      setPendingId(null)
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openDisputeId && <DisputeReviewModal chargebackId={openDisputeId} onClose={() => onOpenDispute(null)} />}
    </div>
  )
}

function ChargebackRow({ c, pending, onOpen, onState }: {
  c: StaffChargeback
  pending: boolean
  onOpen: () => void
  onState: (state: ChargebackState) => void
}) {
  const money = moneyFormatter(c.currency)
  const status = responseStatus(c)
  const reviewable = !!c.provider_dispute_id && c.provider === 'finix'
  const days = c.provider_response_state === 'NEEDS_RESPONSE' && !c.response_action ? daysUntil(c.respond_by) : null
  return (
    <tr className={ROW}>
      <td className={TD}>{fmtDateTime(c.received_at)}</td>
      <td className={TD}>
        {c.event ? (
          <Link to="/events/$eventId/fraud" params={{ eventId: String(c.event) }} search={{ tab: 'chargebacks' }} className="text-blue-600 dark:text-blue-400 hover:underline">
            {c.event_name ?? `Event #${c.event}`}
          </Link>
        ) : EMPTY}
      </td>
      <td className={TD}>
        {c.user ? (
          <Link to="/ops/fraud" search={{ tab: 'attendees', user: c.user }} className="text-blue-600 dark:text-blue-400 hover:underline">
            {c.user_name || `User #${c.user}`}
          </Link>
        ) : (
          <span className="text-neutral-500 dark:text-neutral-400" title="No charge in the ledger carries this transfer, and Finix could not say whose it was">
            No matching charge
          </span>
        )}
      </td>
      <td className={TD}>{money(c.amount)}</td>
      <td className={TD}>
        <select
          value={c.state}
          disabled={pending}
          onChange={(e) => onState(e.target.value as ChargebackState)}
          className={cn(PILL, 'border-0 cursor-pointer', STATE_CHIP[c.state])}
        >
          {CHARGEBACK_STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </td>
      <td className={TD}>{[c.reason, c.reason_code].filter(Boolean).join(' · ') || EMPTY}</td>
      <td className={TD}>
        {c.respond_by ? fmtDateTime(c.respond_by) : EMPTY}
        {days !== null && (
          <span className={cn('ml-1 text-xs', days <= 3 ? 'font-medium text-red-600 dark:text-red-400' : 'text-neutral-500')}>
            {days < 0 ? 'overdue' : `${days}d`}
          </span>
        )}
      </td>
      <td className={TD}>
        {status ? (
          <button
            type="button"
            onClick={reviewable ? onOpen : undefined}
            disabled={!reviewable}
            title={status.title}
            className={cn('inline-flex items-center gap-1', reviewable && 'cursor-pointer hover:opacity-80')}
          >
            <span className={cn(PILL, status.chip)}>{status.label}</span>
            {c.high_priority && (
              <span className={cn(PILL, 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300')} title="This charge was 3-D Secure authenticated">3DS</span>
            )}
          </button>
        ) : EMPTY}
        {status && c.submitted_at && <div className="text-xs text-neutral-500">{fmtDateTime(c.submitted_at)}</div>}
      </td>
      <td className={TD}>
        {c.has_evidence ? (
          <button type="button" onClick={onOpen} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline dark:text-blue-400">
            <FileText className="h-4 w-4" />
            {c.evidence_uploaded_at ? 'Sent PDF' : 'Draft PDF'}
          </button>
        ) : reviewable && status?.group === 'open' ? (
          <button type="button" onClick={onOpen} className="text-sm text-blue-600 hover:underline dark:text-blue-400">Review</button>
        ) : EMPTY}
      </td>
      <td className={TD}><FinixLink id={c.provider_dispute_id} kind="dispute" /></td>
      <td className={TD}><FinixLink id={c.provider_transfer_id} /></td>
    </tr>
  )
}
